// Authentication branches
// -----------------------
// 1) No device key
//    -> Full setup (welcome -> profile -> verify -> switch to authenticator)
// 2) Device key but GBRK for this QR not yet stored
//    -> Confirm & pair -> authenticate -> finish
// 3) Device key and GBRK already known
//    -> Authenticate -> finish
//
// Entry points
// ------------
// A) Deep link (native/custom scheme or web fallback ?u=... or ?wsUrl=...)
//    -> beginHandshakeFromUrl(httpsUrl, app, { shouldReturn: true })
// B) Scan QR
//    -> beginHandshakeFromUrl(httpsUrl, app, { shouldReturn: false })
//
// On finish()
// ------------
// Back stack first, then URL(s), then manual modal.

import dMobileAuthenticationFlow from "./tide-flows/Flows/AuthenticationFlows/dMobileAuthenticationFlow.js";
import { Scanner } from "./platform.js";
import { Storage } from "./StorageManager.js";
import { KeyManager } from "./KeyManager.js";
import { DeviceManager } from "./DeviceManager.js";
import { Notifications } from "./NotificationManager.js";
import { Install } from "./InstallPrompt.js";
import { ensurePersisted } from "./StoragePersistence.js";
import { saveIfConnected } from "./VaultBackup.js";
// Imported (not hardcoded) so the bundler rewrites it to the hashed filename.
// A literal "./assets/default-logo.svg" in runtime-generated HTML 404s into the
// SPA fallback and renders as a broken image.
import defaultLogoUrl from "./assets/default-logo.svg";
import { INACTIVITY_TIMEOUT_MS, GBRK_STORAGE_KEY } from "./constants.js";
import { getServiceNameFromUrl, getRegistrableDomain } from "./DomainParser.js";
import { createNewTideKey } from "./utils.js";
import { Cryptide } from "@tideorg/js";

const { Serialization } = Cryptide;

// Device auth at app boundaries
let lastAuthAt = 0;
let authInFlight = false;

// Small guard to avoid back-to-back prompts
const MIN_AUTH_INTERVAL_MS = 1500;
let authSuppressedUntil = 0;

// First-run suppression: block any boundary prompts until setup completes
let suppressAuthPrompts = false;

// Track when the app backgrounded to enforce "Lock after"
let lastBackgroundAt = 0;

// App Lock defaults
const APP_LOCK_ENABLED_KEY = "app_lock_enabled";
const LOCK_AFTER_MS_KEY = "app_lock_lock_after_ms";
const DEFAULT_APP_LOCK_ENABLED = true;
const DEFAULT_LOCK_AFTER_MS = 60 * 1000;
const LOCK_AFTER_CHOICES = [
  { label: "Immediately", ms: 0 },
  { label: "30 seconds", ms: 30 * 1000 },
  { label: "1 minute", ms: 60 * 1000 },
  { label: "5 minutes", ms: 5 * 60 * 1000 },
];

// App Lock state (in-memory mirror of Storage)
let appLockEnabled = DEFAULT_APP_LOCK_ENABLED;
let lockAfterMs = DEFAULT_LOCK_AFTER_MS;

const ENTRY = { DEEP_LINK: "deep_link", REGULAR: "regular" };

/* ------------ Deep-link single-use store ------------ */
const DeepLinkStore = {
  KEY: "__tide_wsurl__",
  set(url) { try { url && localStorage.setItem(this.KEY, url); } catch { } },
  get() { try { return localStorage.getItem(this.KEY) || null; } catch { return null; } },
  has() { return !!this.get(); },
  clear() { try { localStorage.removeItem(this.KEY); } catch { } },
  consume() { const v = this.get(); this.clear(); return v; },
};

/* ------------ App Lock settings persistence ------------ */
async function loadAppLockSettings() {
  try {
    const enabledRaw = await Storage.get(APP_LOCK_ENABLED_KEY);
    const afterRaw = await Storage.get(LOCK_AFTER_MS_KEY);
    appLockEnabled = enabledRaw == null ? DEFAULT_APP_LOCK_ENABLED : enabledRaw === "true";
    lockAfterMs = afterRaw == null ? DEFAULT_LOCK_AFTER_MS : Math.max(0, parseInt(afterRaw, 10) || 0);
  } catch {
    appLockEnabled = DEFAULT_APP_LOCK_ENABLED;
    lockAfterMs = DEFAULT_LOCK_AFTER_MS;
  }
  try { KeyManager.setBackgroundGraceMs?.(lockAfterMs); } catch { }
}

async function saveAppLockSettings({ enabled, ms }) {
  appLockEnabled = !!enabled;
  lockAfterMs = Math.max(0, ms | 0);
  try {
    await Storage.set(APP_LOCK_ENABLED_KEY, String(appLockEnabled));
    await Storage.set(LOCK_AFTER_MS_KEY, String(lockAfterMs));
  } finally {
    // Re-evaluate inactivity watcher whenever settings change
    stopInactivityWatcher();
    startInactivityWatcher();
  }
  try { KeyManager.setBackgroundGraceMs?.(lockAfterMs); } catch { }
}

/* ------------ Inline base styles injection ------------ */
function injectBaseStylesOnce() {
  if (document.getElementById("_tide_ui_styles")) return;
  const style = document.createElement("style");
  style.id = "_tide_ui_styles";
  // Neo-brutalist layer: only structural overrides the stylesheet cannot carry
  // (z-index layering + safe-area padding for dynamically created overlays).
  // All visual tokens live in index.css.
  style.textContent = `
    :root {
      --tide-z-overlay: 10000;
      --tide-z-fab: 10001;
    }
    .modal-backdrop {
      z-index: var(--tide-z-overlay);
      padding: calc(env(safe-area-inset-top, 0) + 10px) 12px calc(env(safe-area-inset-bottom, 0) + 10px);
    }
    .toast-stack { z-index: var(--tide-z-overlay); }
    .tide-overlay { z-index: var(--tide-z-fab); }
    select.input {
      width: 100%; background: var(--paper); color: var(--ink);
      border: var(--bw) solid var(--border-strong); border-radius: var(--r-sm);
      padding: 12px 14px; outline: none; font-size: 15px;
      font-family: var(--font-body);
    }
    select.input:focus { border-color: var(--cyan); box-shadow: var(--shadow-blue); }
    .floating-restart.hidden { display: none !important; }
  `;
  document.head.appendChild(style);
}

/* ------------ Utilities ------------ */

function escapeHtml(s = "") {
  return s.replace(/[&<>\"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

// Field error helpers
function clearFieldError(input) {
  if (!input) return;
  input.classList.remove("error");
  const next = input.nextElementSibling;
  if (next && next.classList.contains("field-error")) next.remove();
}
function setFieldError(input, message) {
  if (!input) return;
  clearFieldError(input);
  input.classList.add("error");
  const err = document.createElement("div");
  err.className = "field-error";
  err.textContent = message;
  input.insertAdjacentElement("afterend", err);
}

// Validations
function isUsernameValid(value) {
  // Can be anything except spaces
  if (!value) return false;
  if (/\s/.test(value)) return false;
  if (value.length > 64) return false;
  return true;
}
function isEmailValid(value) {
  if (!value) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function extractUrlFromDeepLink(input) {
  if (!input) return null;
  const safeDecode = (v) => { try { v = decodeURIComponent(v); } catch { } try { v = decodeURIComponent(v); } catch { } return v.trim(); };
  const getParams = (raw) => {
    try {
      if (raw.startsWith("?")) return new URLSearchParams(raw);
      if (raw.includes("://")) return new URL(raw, window.location.origin).searchParams;
      return new URLSearchParams(raw);
    } catch { return new URLSearchParams(); }
  };
  let params = getParams(input);
  let candidate = params.get("wsUrl") ?? params.get("u");
  if (!candidate && input.includes("://")) {
    try {
      const urlObj = new URL(input, window.location.origin);
      if (urlObj.hash && urlObj.hash.length > 1) {
        const hashParams = new URLSearchParams(urlObj.hash.slice(1));
        // FIX: read "u" from hash, not from params again
        candidate = hashParams.get("wsUrl") ?? hashParams.get("u");
      }
    } catch { }
  }
  if (!candidate) return null;
  candidate = safeDecode(candidate);
  if (!/^(wss?|https?):\/\//i.test(candidate)) return null;
  try { return new URL(candidate).toString(); } catch { return null; }
}

function normalizeToHttpForHandshake(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.protocol === "ws:") { u.protocol = "http:"; return u.toString(); }
    if (u.protocol === "wss:") { u.protocol = "https:"; return u.toString(); }
    if (u.protocol === "http:" || u.protocol === "https:") return u.toString();
  } catch { }
  return null;
}

/** ---------- Build endpoints from voucher URL (fallback) ---------- */
function buildReturnEndpointsFromVoucherUrl(voucherUrlEncoded) {
  if (!voucherUrlEncoded) return null;
  let decoded = voucherUrlEncoded; try { decoded = decodeURIComponent(decoded); } catch { }
  let voucher; try { voucher = new URL(decoded); } catch { return null; }

  const realmMatch = voucher.pathname.match(/\/realms\/([^/]+)/);
  if (!realmMatch) return null;
  const realm = realmMatch[1];

  const sessionId = voucher.searchParams.get("sessionId");
  const tabId = voucher.searchParams.get("tabId");
  const clientId = voucher.searchParams.get("clientId");
  if (!sessionId || !tabId || !clientId) return null;

  const base = `${voucher.origin}/realms/${encodeURIComponent(realm)}/tide/v1/sessions/${encodeURIComponent(sessionId)}/tabs/${encodeURIComponent(tabId)}/clients/${encodeURIComponent(clientId)}`;
  return {
    returnInfoUrl: `${base}/return-info`,
    redirectUriUrl: `${base}/redirect-uri`
  };
}

/** ---------- Fetch iOS "go back" URL (mobile-side only) ---------- */
async function fetchIOSReturnURL({ endpointUrl, voucherUrl }) {
  if (endpointUrl) {
    try {
      const r = await fetch(endpointUrl, { method: "GET", credentials: "omit", cache: "no-store" });
      if (r.ok) return (await r.text() || "").trim();
    } catch { }
  }
  const ep = buildReturnEndpointsFromVoucherUrl(voucherUrl);
  if (ep) {
    try {
      const r1 = await fetch(ep.returnInfoUrl, { method: "GET", credentials: "omit", cache: "no-store" });
      if (r1.ok) {
        const j = await r1.json().catch(() => null);
        if (j?.return_url) return String(j.return_url);
        if (j?.redirect_uri) return String(j.redirect_uri);
      }
    } catch { }
    try {
      const r2 = await fetch(ep.redirectUriUrl, { method: "GET", credentials: "omit", cache: "no-store" });
      if (r2.ok) return (await r2.text() || "").trim();
    } catch { }
  }
  return null;
}

/* ------------ UI helpers ------------ */

function srAnnounce(msg) {
  let el = document.getElementById("sr-live");
  if (!el) {
    el = document.createElement("div");
    el.id = "sr-live";
    el.className = "sr-only";
    el.setAttribute("aria-live", "polite");
    el.setAttribute("aria-atomic", "true");
    document.body.appendChild(el);
  }
  el.textContent = "";
  setTimeout(() => { el.textContent = msg; }, 20);
}

// Is any modal open?
function isModalOpen() {
  try { return document.body.classList.contains("has-modal"); } catch { return false; }
}

// Close all modals safely
function closeAllModals() {
  try {
    document.querySelectorAll(".modal-backdrop").forEach(n => n.remove());
    document.body.classList.remove("has-modal");
  } catch { }
}

// Button busy controller
function busyController(btn) {
  if (!btn) return { start() { }, step() { }, state() { }, done() { }, isActive() { return false; } };
  let active = false;
  const original = btn.innerHTML || btn.textContent;

  function render(label = "Working...") {
    btn.innerHTML = `<span class="spinner" aria-hidden="true"></span><span class="spinner-label">${escapeHtml(label)}</span>`;
  }

  return {
    start(label = "Working...") {
      if (active) return;
      active = true;
      btn.dataset.prevHtml = original;
      btn.classList.add("is-busy");
      btn.disabled = true;
      btn.setAttribute("aria-busy", "true");
      render(label);
      srAnnounce(label);
    },
    step(label = "") {
      if (!active) return;
      if (label) {
        const lab = btn.querySelector(".spinner-label");
        if (lab) lab.textContent = label; else render(label);
        srAnnounce(label);
      }
    },
    state(variant = "", label = "") {
      if (!active) return;
      btn.classList.toggle("is-redirecting", variant === "redirecting");
      if (label) this.step(label);
    },
    done() {
      if (!active) return;
      active = false;
      btn.classList.remove("is-busy", "is-redirecting");
      btn.disabled = false;
      btn.removeAttribute("aria-busy");
      btn.innerHTML = btn.dataset.prevHtml || original;
      delete btn.dataset.prevHtml;
    },
    isActive() { return active; },
  };
}

function toastStack() {
  let stack = document.getElementById("toastStack");
  if (!stack) {
    stack = document.createElement("div");
    stack.id = "toastStack";
    stack.className = "toast-stack";
    stack.setAttribute("aria-live", "polite");
    document.body.appendChild(stack);
  }
  return stack;
}

function toast(msg, type = "info") {
  const el = document.createElement("div");
  el.className = "toast";
  if (type === "error") el.classList.add("toast-error");
  if (type === "success") el.classList.add("toast-success");
  el.textContent = msg;
  // Into the stack, so concurrent toasts queue instead of overlapping.
  toastStack().appendChild(el);
  setTimeout(() => el.remove(), 3000);
}

// Basic modal system
function modal(templateHTML, onMount, opts = {}) {
  const wrap = document.createElement("div");
  const extra = opts.center ? " center" : (opts.align ? ` align-${opts.align}` : "");
  wrap.className = "modal-backdrop";
  wrap.innerHTML = `<div class="modal-card${extra}" role="dialog" aria-modal="true">${templateHTML}</div>`;
  document.body.appendChild(wrap);
  document.body.classList.add("has-modal");

  const focusables = wrap.querySelectorAll('button,[href],input,textarea,select,[tabindex]:not([tabindex="-1"])');
  const first = focusables[0], last = focusables[focusables.length - 1];

  function trap(e) {
    if (e.key !== "Tab") return;
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
  }
  function onEsc(e) { if (e.key === "Escape") close(); }
  function close() {
    document.removeEventListener("keydown", trap);
    document.removeEventListener("keydown", onEsc);
    wrap.remove();
    document.body.classList.remove("has-modal");
  }

  document.addEventListener("keydown", trap);
  document.addEventListener("keydown", onEsc);
  setTimeout(() => first?.focus(), 0);

  onMount?.(wrap, close);
  return { el: wrap, close };
}

function errorModal({ title = "Something went wrong", message = "", detail = "", primary = "OK", onPrimary, secondary, onSecondary, tertiary, onTertiary }) {
  return modal(`
    <h2 class="modal-title">${escapeHtml(title)}</h2>
    <p class="modal-intro">${escapeHtml(message)}</p>
    ${detail ? `<details class="error-details"><summary>Show technical details</summary><pre style="white-space:pre-wrap;word-break:break-word;">${escapeHtml(detail)}</pre></details>` : ""}
    <div class="modal-actions">
      ${tertiary ? `<button class="btn btn-ghost" id="ter">${escapeHtml(tertiary)}</button>` : ""}
      ${secondary ? `<button class="btn btn-ghost" id="sec">${escapeHtml(secondary)}</button>` : ""}
      <button class="btn btn-primary" id="pri">${escapeHtml(primary)}</button>
    </div>
  `, (root, close) => {
    const pri = root.querySelector("#pri");
    const sec = root.querySelector("#sec");
    const ter = root.querySelector("#ter");
    pri?.addEventListener("click", () => { try { onPrimary?.(); } finally { close(); } });
    if (sec) sec.addEventListener("click", () => { try { onSecondary?.(); } finally { close(); } });
    if (ter) ter.addEventListener("click", () => { try { onTertiary?.(); } finally { close(); } });
  }, { center: true });
}

/* ------------ Network helper ------------ */
async function probeHttpsReachability(httpsUrl, timeoutMs = 3500) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try { await fetch(httpsUrl, { method: "HEAD", cache: "no-store", signal: ctrl.signal }); return true; }
  catch { return false; }
  finally { clearTimeout(t); }
}

/* ------------ Manual return modal (no auto-redirect) ------------ */
function showManualReturnModal({ vendorReturnURL = null } = {}) {
  const registrable = vendorReturnURL ? getRegistrableDomain(vendorReturnURL) : null;
  const domainText = registrable
    ? (typeof registrable === "string" ? escapeHtml(registrable) : escapeHtml(registrable.raw))
    : "your browser";
  return modal(`
    <header class="modal-header">
      <div class="avatar" aria-hidden="true">↩</div>
      <div><h2 class="modal-title">Switch back to your browser</h2></div>
    </header>

    <div class="modal-body">
      <p class="modal-intro">
        You are all set here. To finish, go back to the tab where you started sign-in${vendorReturnURL ? ` (<b>${domainText}</b>)` : ""}.
      </p>

      <div class="steps-card">
        <div class="steps-card__header">
          <svg class="steps-card__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 3l9 4-9 4-9-4 9-4zM3 10l9 4 9-4M3 17l9 4 9-4"
              stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <div class="steps-card__title">What to do</div>
        </div>
        <ol class="steps-list">
          <li>Open your browser app.</li>
          <li>Return to the tab with ${vendorReturnURL ? `<b>${domainText}</b>` : "the site you came from"}.</li>
          <li>If the page does not update, refresh the tab.</li>
        </ol>
      </div>

      <p class="modal-note">Tip: On iOS, use the app switcher; on Android, use the recent apps button.</p>
      <div class="modal-spacer"></div>
    </div>

    <div class="modal-actions">
      <button class="btn btn-primary" id="ok">OK</button>
    </div>
  `, (root, close) => {
    root.querySelector("#ok")?.addEventListener("click", close);
  }, { align: "title-actions" });
}

/* ------------ Return-to-caller helpers ------------ */

// Resolve when the app backgrounds (visibility change) or a timeout hits.
// Returns true if backgrounded.
function waitForBackgroundOrTimeout(ms = 1600) {
  return new Promise((resolve) => {
    let done = false;
    const cleanup = () => { if (done) return; done = true; clearTimeout(t); document.removeEventListener("visibilitychange", onVis); resolve(true); };
    const onVis = () => { if (document.visibilityState !== "visible") cleanup(); };
    const t = setTimeout(() => { if (done) return; done = true; document.removeEventListener("visibilitychange", onVis); resolve(false); }, ms);
    document.addEventListener("visibilitychange", onVis, { once: true });
  });
}

// Try to reveal the previous app/page (best-effort).
// Returns true if we attempted something.
async function attemptRevealPreviousApp() {
  // If we have history, go back (useful if this shell was pushed on top)
  try {
    if (history.length > 1) {
      history.back();
      return true;
    }
  } catch { }

  return false;
}

// Navigate to a URL. Returns true if attempted.
async function returnToVendor(url) {
  if (!url) return false;
  try { window.location.href = url; return true; } catch { }
  try { window.open(url, "_blank"); return true; } catch { }
  return false;
}

// ORDER: Back stack first -> URL(s) -> Manual modal
async function bounceBackToCaller({ vendorReturnURL, iosReturnURL }) {
  // 1) Back stack reveal (prefer cheapest)
  const revealed = await attemptRevealPreviousApp();
  if (revealed) {
    const bg = await waitForBackgroundOrTimeout(900);
    if (bg) return;
  }

  // 2) URL navigation
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const candidates = isIOS
    ? [iosReturnURL, vendorReturnURL].filter(Boolean)
    : [vendorReturnURL].filter(Boolean);

  for (const url of candidates) {
    const attempted = await returnToVendor(url);
    if (!attempted) continue;
    const bg = await waitForBackgroundOrTimeout(1600);
    if (bg) return;
  }

  // 3) Manual fallback
  showManualReturnModal({ vendorReturnURL });
}

/* ------------ Shared handshake path (scan or deep link) ------------ */

let handshakeInFlight = false;

async function beginHandshakeFromUrl(httpsUrl, appInstance, opts = { shouldReturn: false, trackForRetry: false }) {
  if (!httpsUrl) throw new Error("Missing handshake URL");
  if (handshakeInFlight) return null;
  handshakeInFlight = true;

  let enclaveTunnel;
  const cta = busyController(appInstance?.scanBtn);

  try {
    if (opts.trackForRetry && appInstance) { try { appInstance._lastHandshakeUrl = httpsUrl; } catch { } }

    cta.start("Connecting...");

    const reachable = await probeHttpsReachability(httpsUrl);
    if (!reachable) {
      cta.done();
      appInstance?.showOfflineModal(httpsUrl);
      return null;
    }

    cta.step("Opening secure tunnel...");
    const wsUrl = httpToWs(httpsUrl);
    enclaveTunnel = new dMobileAuthenticationFlow(wsUrl);

    cta.step("Verifying session...");
    let userName = await KeyManager.getUserInfo("UserName");
    if (!isUsernameValid(String(userName || ""))) {
      await appInstance?.showUserProfileSetupWizard();
      userName = await KeyManager.getUserInfo("UserName");
    }

    // Validate username for safety on mobile
    if (!isUsernameValid(String(userName || ""))) {
      await appInstance?.confirmAndClearUsername();
      throw new Error("Invalid username. Usernames cannot contain spaces.");
    }

    cta.step("Checking your account...");
    const existingUser = await userExists(httpsUrl, userName);

    cta.step("Securing session...");
    const enclaveData = await appInstance?.getInfoFromTunnel(enclaveTunnel);
    if (!enclaveData) throw new Error("Failed to receive data from secure tunnel");

    let iosReturnURL = null;
    try {
      iosReturnURL = await fetchIOSReturnURL({
        endpointUrl: enclaveData.userReturnURL || null,
        voucherUrl: enclaveData.voucherURL || null,
      });
    } catch { }

    try {
      appInstance._lastVendorReturnURL = enclaveData.vendorReturnURL || null;
      appInstance._lastIOSReturnURL = iosReturnURL || null;
      appInstance._lastEnclaveBase = httpsUrl || null;
      appInstance._lastServiceName = getServiceNameFromUrl(enclaveData.vendorReturnURL) || "";
    } catch { }

    // The tunnel is open and the vendor identified - if the user has since
    // backgrounded the app, tell them a sign-in is waiting.
    Notifications.signInRequested(appInstance?._lastServiceName);

    await requireDeviceAuthIfNeeded("handshake_auth", { force: !KeyManager.isSessionActive?.() });

    const deviceKey = await KeyManager.getCachedDeviceKey();

    if (deviceKey === null) {
      cta.step("Review and confirm...");
      const confirmed = await appInstance?.showConfirmationPage(enclaveData);
      if (!confirmed) { cta.done(); appInstance?.showStatus("Setup cancelled", "info"); return null; }
      if (existingUser) { await appInstance?.showSetupWizardFinal(enclaveTunnel, enclaveData, opts); }
      else { await appInstance?.showSetupWizardFinalNewTideAcc(enclaveTunnel, enclaveData, opts); }
      DeepLinkStore.clear();
      cta.done();
      return enclaveData;
    }

    const knownGBRK = enclaveData.browserKeyIdentifier || null;
    const hasGBRK = knownGBRK === null ? false : await KeyManager.hasGBRK(knownGBRK, { deviceKey });
    if (!hasGBRK) {
      cta.step("Confirm this service...");
      const confirmed = await appInstance?.showConfirmationPage(enclaveData);
      if (!confirmed) { cta.done(); appInstance?.showStatus("Setup cancelled", "info"); return null; }
      await KeyManager.addGBRK(enclaveData.browserKeyIdentifier, { deviceKey });
    }

    if (existingUser) {
      cta.step("Authenticating...");
      if (typeof enclaveTunnel.authenticate === "function") {
        await enclaveTunnel.authenticate(deviceKey);
      } else {
        throw new Error("No authenticate method available");
      }
    } else {
      cta.step("Creating account...");
      const deviceName = await DeviceManager.getDeviceName();
      const email = await KeyManager.getUserInfo("Email");
      if (typeof enclaveTunnel.createNewAccount === "function") {
        await enclaveTunnel.createNewAccount(deviceKey, deviceName, email);
      } else {
        throw new Error("No account creation method available");
      }
    }

    DeepLinkStore.clear();

    if (opts.shouldReturn) {
      cta.state("redirecting", appInstance?._lastIOSReturnURL ? "Opening browser..." : "Ready - switch back to your browser");
      try {
        await bounceBackToCaller({
          vendorReturnURL: appInstance?._lastVendorReturnURL,
          iosReturnURL: appInstance?._lastIOSReturnURL
        });
      } finally {
        setTimeout(() => cta.done(), 1000);
        try {
          appInstance._lastVendorReturnURL = null;
          appInstance._lastIOSReturnURL = null;
          appInstance._lastEnclaveBase = null;
        } catch { }
      }
    } else {
      cta.done();
    }

    appInstance?.showRestart(false);
    return enclaveData;

  } catch (err) {
    cta.done();
    DeepLinkStore.clear();

    // Provide a clear way to go back home without restarting the app
    errorModal({
      title: "Authentication Error",
      message: "We could not complete authentication. Try Again.",
      detail: String(err?.stack || err?.message || err),
      primary: "Close",
      onPrimary: () => { },
    });

    return null;
  } finally {
    try { await enclaveTunnel?.finish?.(); } catch { }
    handshakeInFlight = false;
  }
}

/* ------------ App ------------ */

class QRScannerApp {
  constructor(entryMode = ENTRY.REGULAR) {
    injectBaseStylesOnce();

    if (!document.getElementById("sr-live")) {
      const sr = document.createElement("div");
      sr.id = "sr-live"; sr.className = "sr-only";
      sr.setAttribute("aria-live", "polite"); sr.setAttribute("aria-atomic", "true");
      document.body.appendChild(sr);
    }
    this.entryMode = entryMode;
    this._lastHandshakeUrl = null;
    this._lastVendorReturnURL = null;
    this._lastIOSReturnURL = null;
    this._lastEnclaveBase = null;
    this.scannedCodes = new Map();
    this._inScan = false;

    this._overlay = null;
    this.scanBtn = null;
    this.statusElement = null;
    this.restartBtn = null;
    this.securityBtn = null;

    this.initializeUI();
    this.loadScannedCodes();
  }

  ensureOverlayToolbar() {
    if (this._overlay && document.body.contains(this._overlay)) return this._overlay;
    const overlay = document.createElement("div");
    overlay.className = "tide-overlay";
    overlay.id = "tideOverlayToolbar";
    document.body.appendChild(overlay);
    this._overlay = overlay;
    return overlay;
  }

  initializeUI() {
    // Main controls
    this.scanBtn = document.getElementById("scanQrBtn");
    this.statusElement = document.getElementById("status");

    if (this.scanBtn) {
      this.scanBtn.classList.remove("hidden");
      this.scanBtn.disabled = false;
      this.scanBtn.onclick = () => this.scanOrUseDeepLink();
    }

    // Floating overlay toolbar to avoid layout shift
    const overlay = this.ensureOverlayToolbar();

    // Restart FAB (hidden by default)
    this.restartBtn = document.getElementById("restartFlowBtn");
    if (!this.restartBtn) {
      this.restartBtn = document.createElement("button");
      this.restartBtn.id = "restartFlowBtn";
      this.restartBtn.className = "floating-restart tide-fab hidden";
      this.restartBtn.title = "Restart setup";
      this.restartBtn.innerHTML = `<span class="dot" aria-hidden="true"></span><span>Restart</span>`;
    } else {
      this.restartBtn.classList.add("tide-fab");
      this.restartBtn.classList.add("hidden");
    }
    this.restartBtn.onclick = () => this.confirmAndRestart();
    overlay.appendChild(this.restartBtn);

    // Security FAB
    this.securityBtn = document.getElementById("securityBtn");
    if (!this.securityBtn) {
      this.securityBtn = document.createElement("button");
      this.securityBtn.id = "securityBtn";
      this.securityBtn.className = "tide-fab";
      this.securityBtn.title = "Security settings";
      this.securityBtn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 2l7 4v6c0 5-3.5 9.3-7 10-3.5-.7-7-5-7-10V6l7-4zm0 3.2L7 7.4v4.6c0 3.7 2.6 7.2 5 7.9 2.4-.7 5-4.2 5-7.9V7.4l-5-2.2zM11 8h2v5h-2V8zm0 6h2v2h-2v-2z"/></svg><span>Settings</span>`;
    } else {
      this.securityBtn.classList.add("tide-fab");
      this.securityBtn.textContent = "Security";
    }
    this.securityBtn.onclick = () => this.showSecuritySettings();
    overlay.appendChild(this.securityBtn);
  }

  setEntryMode(mode) {
    this.entryMode = mode;
    if (this.scanBtn) {
      this.scanBtn.classList.remove("hidden");
      this.scanBtn.disabled = false;
      this.scanBtn.onclick = () => this.scanOrUseDeepLink();
    }
  }

  showRestart(visible) {
    if (!this.restartBtn) return;
    this.restartBtn.classList.toggle("hidden", !visible);
  }

  async confirmAndRestart(reasonText = "Start over and clear this device?") {
    errorModal({
      title: "Restart Setup",
      message: reasonText,
      primary: "Restart",
      secondary: "Cancel",
      onPrimary: async () => { await this.clearAppSettings(); },
    });
  }

  async confirmAndClearUsername(reasonText = "Start over and use a different username?") {
    errorModal({
      title: "Change Username",
      message: reasonText,
      primary: "Clear username",
      secondary: "Cancel",
      onPrimary: async () => {
        await this.clearSavedUsername();
        closeAllModals();
        await this.goHome();
        toast("Username cleared. You can enter a new one.", "success");
      },
    });
  }

  // Reset everything and show welcome
  async clearAppSettings() {
    try {
      closeAllModals();
      await this.resetAppState();
      await this.showSetupWizardInitial();
      if (this.scanBtn) {
        this.scanBtn.disabled = false;
        this.scanBtn.classList.remove("hidden");
      }
    } catch (e) { console.warn("clearAppSettings failed:", e); }
  }

  // Pure navigation back to welcome (no resets)
  async goHome() {
    try {
      closeAllModals();
      await this.showSetupWizardInitial();
      if (this.scanBtn) {
        this.scanBtn.disabled = false;
        this.scanBtn.classList.remove("hidden");
      }
    } catch (e) {
      console.warn("goHome failed:", e);
    }
  }

  async clearSavedUsername() {
    try {
      if (typeof KeyManager.setUserInfo === "function") {
        await KeyManager.setUserInfo("UserName", "");
      } else if (typeof KeyManager.storeUserInfo === "function") {
        await KeyManager.storeUserInfo({ user: { UserName: "" } });
      }
    } catch (e) { console.warn("Failed to clear username:", e); }
  }

  async resetAppState() {
    try { await KeyManager.wipeAllLocal?.(); } catch { }
    try { await Storage.remove?.("scanned_codes"); } catch { }
    try { await Storage.set("scanned_codes", ""); } catch { }
    try { await Storage.set(GBRK_STORAGE_KEY, ""); } catch { }
    try { await Storage.set("first_run_complete", ""); } catch { }
    DeepLinkStore.clear();
    this.scannedCodes.clear();
    lastAuthAt = 0;
    suppressAuthPrompts = false;
    this.showRestart(false);
  }

  async loadScannedCodes() {
    try {
      const raw = await Storage.get("scanned_codes");
      const data = raw ? JSON.parse(raw) : {};
      Object.entries(data).forEach(([id, code]) => this.scannedCodes.set(id, code));
    } catch { this.showStatus("Failed to load scanned codes", "error"); }
  }

  async saveScannedCodes() {
    try { await Storage.set("scanned_codes", JSON.stringify(Object.fromEntries(this.scannedCodes))); }
    catch { this.showStatus("Failed to save scanned codes", "error"); }
  }

  /** Prefer a stored deep-link (consumed once) then fall back to camera scan. */
  async scanOrUseDeepLink() {
    await requireDeviceAuthIfNeeded("scan_action", { force: !KeyManager.isSessionActive?.() });

    const consumed = DeepLinkStore.consume();
    if (consumed) {
      const httpsForHandshake = normalizeToHttpForHandshake(consumed);
      if (httpsForHandshake) {
        const busy = busyController(this.scanBtn);
        if (!busy.isActive()) busy.start("Preparing sign-in...");
        await beginHandshakeFromUrl(httpsForHandshake, this, { shouldReturn: true, trackForRetry: false });
        return;
      }
    }
    await this.scanQR();
  }

  // Camera scan -> shared handshake path
  async scanQR() {
    if (this._inScan) return;
    this._inScan = true;

    const busy = busyController(this.scanBtn);
    busy.start("Opening camera...");

    try {
      if (!navigator.onLine) { this.showOfflineModal(); return; }

      const qrData = await Scanner.scan();
      const raw = (qrData || "").trim();
      if (!raw) throw new Error("No QR code detected");
      busy.step("QR detected");

      let originalUrl; try { originalUrl = new URL(raw); } catch { throw new Error("Invalid QR URL"); }
      const httpsUrl = originalUrl.toString();
      const url = extractUrlFromDeepLink(httpsUrl);

      await beginHandshakeFromUrl(url, this, { shouldReturn: false, trackForRetry: true });
    } catch (err) {
      console.error(err);
      this.showStatus(err?.message || "Scan failed", "error");
    } finally {
      busy.done();
      setTimeout(() => { this._inScan = false; }, 300);
    }
  }

  async handleFatalConnectionError(err, host) {
    errorModal({
      title: "Secure Tunnel Error",
      message: "We could not open a secure connection to the service.",
      detail: `URL: ${host}\n${String(err?.stack || err?.message || err)}`,
      primary: "Start Over",
      secondary: "Close",
      onPrimary: async () => { await this.goHome(); },
    });
    toast("Connection error - starting over", "error");
  }

  async getInfoFromTunnel(enclaveTunnel) {
    try {
      const userName = await KeyManager.getUserInfo("UserName");
      const data = await enclaveTunnel.ensureReady(userName);
      return data;
    } catch (err) {
      console.error(err);
      errorModal({
        title: "Session Error",
        message: "We could not verify the session.",
        detail: String(err?.stack || err?.message || err),
        primary: "Start Over",
        secondary: "Close",
        onPrimary: async () => { await this.goHome(); },
      });
      throw new Error("Failed to retrieve secure session data");
    }
  }

  async showConfirmationPage(enclaveData) {
    if (!enclaveData?.vendorReturnURL) throw new Error("Invalid QR data: missing vendorReturnURL");
    const registrableDomain = getRegistrableDomain(enclaveData.vendorReturnURL);
    const serviceName = getServiceNameFromUrl(enclaveData.vendorReturnURL) || "";
    const domainText = typeof registrableDomain === "string" ? escapeHtml(registrableDomain) : escapeHtml(registrableDomain.raw);

    return new Promise((resolve, reject) => {
      let settled = false;
      const succeed = () => { if (settled) return; settled = true; resolve(true); };
      const fail = (err) => { if (settled) return; settled = true; err ? reject(err) : resolve(false); };

      modal(`
        <header class="modal-header">
          <div class="avatar" aria-hidden="true">${(serviceName || "S").slice(0, 1)}</div>
          <div><h2 class="modal-title">Confirm Setup</h2></div>
        </header>

        <div class="modal-body">
          <div class="domain-card" role="group" aria-label="Vendor domain">
            <div class="domain-label">Vendor Domain</div>
            <div class="domain-value" aria-live="polite">${domainText}</div>
          </div>
          <p class="modal-description">
            You are about to connect this device to the service shown above. Make sure it matches the site you intended.
          </p>
          <div class="modal-spacer"></div>
        </div>

        <div class="modal-actions">
          <button class="btn btn-ghost" id="cancel" type="button">Cancel</button>
          <button class="btn btn-primary" id="confirm" type="button">Confirm & Setup</button>
        </div>
      `, (root, close) => {
        const confirmBtn = root.querySelector("#confirm");
        const cancelBtn = root.querySelector("#cancel");
        const confirmBusy = busyController(confirmBtn);

        cancelBtn?.addEventListener("click", () => { close(); fail(); });
        confirmBtn?.addEventListener("click", async () => {
          confirmBusy.start("Confirming...");
          try { toast("Confirmed", "success"); close(); succeed(); }
          catch (err) { toast("Failed to confirm", "error"); fail(err); }
          finally { confirmBusy.done(); }
        });
      }, { align: "title-actions" });
    });
  }

  // Initial wizard
  async showSetupWizardInitial() {
    const deeplinkAvailable = DeepLinkStore.has();
    modal(`
      <div class="wizard-step step-1">
        <h2 class="modal-title">Welcome to Tide Authenticator</h2>
        <p class="modal-intro welcome-lede">
          ${deeplinkAvailable
        ? "We detected a sign-in link from your browser. You can continue without scanning."
        : "Let us guide you through setting up this device to sign in securely:"
      }
        </p>
        <div class="steps-card">
          <div class="steps-card__header">
            <svg class="steps-card__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true" width="18" height="18">
              <path d="M12 3l9 4-9 4-9-4 9-4zM3 10l9 4 9-4M3 17l9 4 9-4"
                stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <div class="steps-card__title">What you will do</div>
          </div>
          ${deeplinkAvailable ? `
            <ol class="steps-list">
              <li>Tap Continue to use the link we received.</li>
              <li>If it expires, you can scan a QR instead.</li>
            </ol>
          ` : `
            <ol class="steps-list">
              <li>Open the site to sign in (or sign up).</li>
              <li>Find the QR code.</li>
              <li>Tap "Get Started" to scan and pair this device.</li>
            </ol>
          `}
        </div>
        <div class="modal-actions">
          <button class="btn btn-ghost" id="close" style="flex:1">Close</button>
          ${deeplinkAvailable
        ? `<button class="btn btn-primary" id="continue" style="flex:1">Continue</button>`
        : `<button class="btn btn-primary" id="start" style="flex:1">Get Started</button>`
      }
        </div>
      </div>
    `, (root, close) => {
      root.querySelector("#close")?.addEventListener("click", close);

      const goWithDeepLink = async () => {
        const consumed = DeepLinkStore.consume();
        const httpsForHandshake = normalizeToHttpForHandshake(consumed);
        close();
        if (httpsForHandshake) {
          const busy = busyController(this.scanBtn);
          if (!busy.isActive()) busy.start("Preparing sign-in...");
          await beginHandshakeFromUrl(httpsForHandshake, this, { shouldReturn: true, trackForRetry: false });
        } else {
          await this.scanQR();
        }
      };

      root.querySelector("#continue")?.addEventListener("click", goWithDeepLink);
      root.querySelector("#start")?.addEventListener("click", goWithDeepLink);
    }, { align: "center" });
  }

  // Profile wizard (first run) with validation. Single biometric prompt lives here.
  async showUserProfileSetupWizard() {
    const deviceName = await DeviceManager.getDeviceName();
    return new Promise((resolve) => {
      modal(`
        <h2 class="modal-title">Set up your profile</h2>
        <div class="field-set">
          <div class="field">
            <label class="field-label" for="wizUsername">Tide Username</label>
            <input id="wizUsername" class="input" placeholder="Enter your Tide username" />
          </div>
          <div class="field">
            <label class="field-label" for="wizDeviceName">Device name</label>
            <input id="wizDeviceName" class="input" value="${escapeHtml(deviceName)}" />
            <p class="field-hint">Used to identify this device.</p>
          </div>
        </div>
        <div class="modal-actions">
          <button class="btn btn-ghost" id="cancel">Cancel</button>
          <button class="btn btn-primary" id="save">Save</button>
        </div>
      `, (root, close) => {
        const saveBtn = root.querySelector("#save");
        const cancelBtn = root.querySelector("#cancel");
        const saveBusy = busyController(saveBtn);
        const uEl = root.querySelector("#wizUsername");
        const dEl = root.querySelector("#wizDeviceName");

        // Live validation
        const validate = () => {
          clearFieldError(uEl);
          const u = (uEl.value || "").trim();
          if (!isUsernameValid(u)) {
            setFieldError(uEl, "Username cannot contain spaces.");
            return false;
          }
          return true;
        };
        uEl.addEventListener("input", validate);
        uEl.addEventListener("keydown", e => { if (e.key === "Enter") saveBtn.click(); });

        cancelBtn?.addEventListener("click", () => { close(); resolve(false); });

        saveBtn?.addEventListener("click", async () => {
          const username = (uEl.value || "").trim();
          const deviceNameInput = (dEl.value || "").trim() || deviceName;

          if (!isUsernameValid(username)) {
            setFieldError(uEl, "Username cannot contain spaces.");
            uEl.focus();
            return;
          }

          try {
            // Optional one-time biometric on profile creation
            saveBusy.start("Authenticating...");
            if (typeof KeyManager.verifyIdentityOnly === "function") {
              await KeyManager.verifyIdentityOnly({ reason: "Set up profile", title: "Authenticate", subtitle: "Use biometrics or device passcode" });
            }
          } catch (e) {
            saveBusy.done();
            toast("Authentication cancelled", "error");
            return;
          }

          saveBusy.step("Saving...");
          try {
            await KeyManager.storeUserInfo({ user: { UserDevice: deviceNameInput, UserName: username } });
            close(); resolve(true);
          } catch {
            toast("Failed to save profile", "error");
          } finally {
            saveBusy.done();
          }
        });
      }, { center: true });
    });
  }

  // Migration wizard (existing Tide user; no device key yet)
  async showSetupWizardFinal(enclaveTunnel, enclaveData, opts = {}) {
    const savedUsername = await KeyManager.getUserInfo("UserName");
    modal(`
      <section id="step4">
        <h3 class="section-title">Confirm It is You</h3>

        <div class="field-set">
          <div class="field">
            <label class="field-label" for="wizUsername">Username</label>
            ${savedUsername ? `
              <input id="wizUsername" class="input" value="${escapeHtml(savedUsername)}" readonly aria-readonly="true"/>
              <button class="link-btn" id="restartWrongUser" type="button">Not you? Restart</button>
            ` : `<input id="wizUsername" class="input" autocomplete="username" />`}
          </div>

          <div class="field">
            <label class="field-label" for="wizPassword">Password</label>
            <input id="wizPassword" class="input" type="password" autocomplete="current-password"/>
          </div>
          <p class="field-hint">This is your final password sign-in.</p>
        </div>

        <div class="modal-actions">
          <button class="btn btn-ghost" id="cancel4">Cancel</button>
          <button class="btn btn-primary" id="verify">Sign In</button>
        </div>
      </section>

      <section id="step5" class="hidden">
        <h3 class="section-title">Switch to Authenticator Login</h3>
        <div class="warn-card"><strong>Important:</strong> After confirming, username/password sign-in is disabled.</div>
        <label class="ack-row"><input id="ackCheckbox" type="checkbox"/><span>I understand.</span></label>
        <div class="modal-actions">
          <button class="btn btn-ghost" id="back5">Back</button>
          <button class="btn btn-primary" id="confirm" disabled>Confirm</button>
        </div>
      </section>
    `, (root, close) => {
      const step4 = root.querySelector("#step4");
      const step5 = root.querySelector("#step5");
      const verify = root.querySelector("#verify");
      const cancel4 = root.querySelector("#cancel4");
      const ack = root.querySelector("#ackCheckbox");
      const confirmBtn = root.querySelector("#confirm");
      const restartWrongUser = root.querySelector("#restartWrongUser");

      const userEl = root.querySelector("#wizUsername");
      const passEl = root.querySelector("#wizPassword");

      const verifyBusy = busyController(verify);
      const confirmBusy = busyController(confirmBtn);

      restartWrongUser?.addEventListener("click", async () => {
        close();
        await this.confirmAndClearUsername("We will clear the saved username so you can start over.");
      });
      cancel4?.addEventListener("click", () => { close(); });

      const validate = () => {
        if (!userEl || userEl.readOnly) return true;
        clearFieldError(userEl);
        const u = (userEl.value || "").trim();
        if (!isUsernameValid(u)) {
          setFieldError(userEl, "Username cannot contain spaces.");
          return false;
        }
        return true;
      };
      userEl?.addEventListener("input", validate);

      verify?.addEventListener("click", async () => {
        const username = userEl?.readOnly ? (userEl.value || "").trim() : (userEl?.value || "").trim();
        const password = passEl.value;

        if (!validate()) { userEl.focus(); return; }
        if (!password) { clearFieldError(passEl); setFieldError(passEl, "Password is required."); passEl.focus(); return; }

        verifyBusy.start("Verifying...");
        try {
          const deviceKey = createNewTideKey();
          await KeyManager.storeDeviceKey(deviceKey);
          if (this.verifyLegacyCredentials) { await this.verifyLegacyCredentials({ username, password }); }
          step4.classList.add("hidden");
          step5.classList.remove("hidden");
        } catch (e) { toast("Sign in failed", "error"); }
        finally { verifyBusy.done(); }
      });

      ack?.addEventListener("change", () => { confirmBtn.disabled = !ack.checked; });
      root.querySelector("#back5")?.addEventListener("click", () => { step5.classList.add("hidden"); step4.classList.remove("hidden"); });

      confirmBtn?.addEventListener("click", async () => {
        confirmBusy.start("Finalizing...");
        try {
          const password = passEl.value;
          const deviceKey = await KeyManager.getCachedDeviceKey();
          await KeyManager.addGBRK(enclaveData.browserKeyIdentifier, { deviceKey });
          await enclaveTunnel.pairNewDevice(deviceKey, password, await DeviceManager.getDeviceName());

          suppressAuthPrompts = false;
          await Storage.set("first_run_complete", "1");
          Notifications.devicePaired(this._lastServiceName);
          // There is now a device key worth keeping, and this is inside a user
          // gesture - the conditions under which browsers are most willing to
          // grant persistence. Safe to repeat if the boot-time attempt was
          // refused.
          ensurePersisted();
          // Keep the on-disk copy current. Silent when a file is already
          // connected; a no-op otherwise, so nobody who has not asked for a
          // backup gets a download thrown at them.
          saveIfConnected();

          if (opts.shouldReturn) {
            confirmBusy.state("redirecting", this._lastIOSReturnURL ? "Opening browser..." : "Ready - switch back to your browser");
            try {
              await bounceBackToCaller({
                vendorReturnURL: this._lastVendorReturnURL || enclaveData.vendorReturnURL,
                iosReturnURL: this._lastIOSReturnURL || enclaveData.userReturnURL || null
              });
            } finally {
              setTimeout(() => confirmBusy.done(), 1000);
            }
          } else {
            confirmBusy.done();
          }

          close();
          this.showRestart(false);
        } catch (e) {
          toast("Setup failed", "error");
          Notifications.signInFailed("Setup could not be completed.");
          confirmBtn.disabled = false;
          this.showRestart(true);
          confirmBusy.done();
        }
      });
    }, { center: true });
  }

  // Migration wizard (new Tide user; no device key yet)
  async showSetupWizardFinalNewTideAcc(enclaveTunnel, enclaveData, opts = {}) {
    const savedUsername = await KeyManager.getUserInfo("UserName");
    const savedEmail = await KeyManager.getUserInfo?.("Email");

    modal(`
      <section id="create">
        <h3 class="section-title">Finish setup</h3>
        <p class="subtle">You are about to create your account with this username. Future sign-ins will happen on this device.</p>

        <div class="field">
          <label class="field-label" for="wizUsername">Username</label>
          ${savedUsername ? `
            <div class="input-row">
              <input id="wizUsername" class="input" value="${escapeHtml(savedUsername)}" readonly aria-readonly="true"/>
              <button class="link-btn" id="restartWrongUser" type="button">Use a different username</button>
            </div>
          ` : `<input id="wizUsername" class="input" placeholder="e.g. alex.park" autocomplete="username" spellcheck="false" autofocus />`}
        </div>

        <div class="field">
          <label class="field-label" for="wizEmail">Email</label>
          <input id="wizEmail" class="input" type="email" placeholder="name@example.com"
                 autocomplete="email" inputmode="email" spellcheck="false"
                 ${savedUsername ? "autofocus" : ""} ${savedEmail ? `value="${escapeHtml(savedEmail)}"` : ""}
                 aria-describedby="emailHelp" required />
        </div>

        <div class="modal-actions">
          <button class="btn btn-ghost" id="cancelCreate">Cancel</button>
          <button class="btn btn-primary" id="createBtn" disabled>Create account</button>
        </div>
      </section>
    `, (root, close) => {
      const userEl = root.querySelector("#wizUsername");
      const emailEl = root.querySelector("#wizEmail");
      const createBtn = root.querySelector("#createBtn");
      const cancelBtn = root.querySelector("#cancelCreate");
      const restart = root.querySelector("#restartWrongUser");
      const busy = busyController(createBtn);

      const validate = () => {
        let ok = true;

        if (userEl && !userEl.readOnly) {
          clearFieldError(userEl);
          const u = (userEl.value || "").trim();
          if (!isUsernameValid(u)) { setFieldError(userEl, "Username cannot contain spaces."); ok = false; }
        }

        if (emailEl) {
          clearFieldError(emailEl);
          const e = (emailEl.value || "").trim();
          if (!isEmailValid(e)) { setFieldError(emailEl, "Enter a valid email address."); ok = false; }
        }

        createBtn.disabled = !ok;
        return ok;
      };

      if (userEl && !userEl.readOnly) {
        userEl.addEventListener("input", validate);
        userEl.addEventListener("keydown", e => { if (e.key === "Enter" && !createBtn.disabled) createBtn.click(); });
      }
      emailEl?.addEventListener("input", validate);
      emailEl?.addEventListener("keydown", e => { if (e.key === "Enter" && !createBtn.disabled) createBtn.click(); });

      cancelBtn?.addEventListener("click", () => { close(); });
      restart?.addEventListener("click", async () => { close(); await this.confirmAndRestart("We will clear the app storage so you can start over."); });

      validate();

      createBtn?.addEventListener("click", async () => {
        const username = userEl?.readOnly ? userEl.value : (userEl?.value ?? "").trim();
        const email = (emailEl?.value ?? "").trim();

        if (!validate()) {
          if (userEl && !userEl.readOnly && !isUsernameValid((userEl.value || "").trim())) { userEl.focus(); return; }
          if (!isEmailValid(email)) { emailEl.focus(); return; }
        }

        busy.start("Creating...");
        createBtn.disabled = true;

        try {
          const deviceKey = createNewTideKey();
          await KeyManager.storeDeviceKey(deviceKey);
          await KeyManager.setUserInfo?.("UserName", username);
          await KeyManager.setUserInfo?.("Email", email);

          const deviceName = await DeviceManager.getDeviceName();

          if (typeof enclaveTunnel.createNewAccount === "function") {
            await enclaveTunnel.createNewAccount(deviceKey, deviceName, email);
          } else {
            throw new Error("No account creation method available");
          }

          suppressAuthPrompts = false;
          await Storage.set("first_run_complete", "1");

          if (opts.shouldReturn) {
            busy.state("redirecting", this._lastIOSReturnURL ? "Opening browser..." : "Ready - switch back to your browser");
            try {
              await bounceBackToCaller({
                vendorReturnURL: this._lastVendorReturnURL || enclaveData.vendorReturnURL,
                iosReturnURL: this._lastIOSReturnURL || enclaveData.userReturnURL || null
              });
            } finally {
              setTimeout(() => busy.done(), 1000);
            }
          } else {
            busy.done();
          }

          close();
          this.showRestart(false);
        } catch (e) {
          console.error(e);
          toast("Account creation failed", "error");
          createBtn.disabled = false;
          this.showRestart(true);
          busy.done();
        }
      });
    }, { center: true });
  }

  showOfflineModal(hostTried) {
    modal(`
      <h2 class="modal-title">You are offline</h2>
      <p class="modal-intro">Connect to the internet and try again.</p>
      ${hostTried ? `<div class="kv"><div class="kv-k">Host</div><div class="kv-v">${escapeHtml(hostTried)}</div></div>` : ""}
      <div class="modal-actions">
        <button class="btn btn-ghost" id="home">Go Home</button>
        <button class="btn btn-primary" id="retry">Try Again</button>
      </div>
    `, (root, close) => {
      root.querySelector("#home")?.addEventListener("click", async () => { close(); await this.goHome(); });
      root.querySelector("#retry")?.addEventListener("click", async () => {
        if (navigator.onLine) {
          close();
          const consumed = DeepLinkStore.consume();
          const httpsForHandshake = normalizeToHttpForHandshake(consumed);
          if (httpsForHandshake) { await beginHandshakeFromUrl(httpsForHandshake, this, { shouldReturn: true, trackForRetry: false }); }
          else { await this.scanOrUseDeepLink(); }
        } else {
          toast("Still offline", "error");
        }
      });
    }, { center: true });
  }

  showStatus(msg, type = "info") {
    srAnnounce(msg);
    if (!this.statusElement) { toast(msg, type); return; }
    this.statusElement.textContent = msg;
    this.statusElement.className = `status ${type}`;
    this.statusElement.classList.remove("hidden");
    clearTimeout(this._statusTimer);
    this._statusTimer = setTimeout(() => this.statusElement.classList.add("hidden"), 2800);
  }

  // Security settings UI (App Lock)
  async showSecuritySettings() {
    if (document.querySelector(".modal-backdrop .modal-title")?.textContent === "Security") return;

    const enabled = appLockEnabled;
    const current = lockAfterMs;

    const optionsHtml = LOCK_AFTER_CHOICES.map(opt => {
      const sel = opt.ms === current ? "selected" : "";
      return `<option value="${opt.ms}" ${sel}>${opt.label}</option>`;
    }).join("");

    modal(`
    <h2 class="modal-title settings-title">Security</h2>

    <section class="settings-group">
      <label class="setting-row" for="appLockToggle">
        <input id="appLockToggle" type="checkbox" ${enabled ? "checked" : ""}/>
        <span class="setting-row__label">App Lock</span>
      </label>
      <p class="setting-hint">Require biometrics or your device passcode to open the app.</p>

      <div class="setting-field">
        <label class="field-label" for="lockAfterSel">Lock after</label>
        <select id="lockAfterSel" class="input">
          ${optionsHtml}
        </select>
        <p class="setting-hint">Re-authenticate after inactivity, or when returning from the background.</p>
      </div>
    </section>

    <section class="settings-group">
      <label class="setting-row" for="notifyToggle">
        <input id="notifyToggle" type="checkbox" ${Notifications.isEnabled() ? "checked" : ""} ${Notifications.isSupported() ? "" : "disabled"}/>
        <span class="setting-row__label">Notifications</span>
      </label>
      <p class="setting-hint" id="notifyHint">${Notifications.isSupported()
        ? (Notifications.permission() === "denied"
          ? "Blocked in your browser settings - allow notifications for this site to turn this on."
          : "Get alerted when a sign-in needs approval or finishes, while the app is in the background.")
        : "This browser does not support notifications."}</p>
    </section>

    <section class="settings-group">
      <h3 class="settings-group__title">Account tools</h3>
      <button class="btn btn-ghost settings-action" id="clearUsernameBtn" type="button">Clear username</button>
      <p class="setting-hint">Only removes the saved username. Device key and pairing remain intact.</p>
    </section>

    <section class="settings-group settings-group--danger">
      <h3 class="settings-group__title settings-group__title--danger">Danger zone</h3>
      <button class="btn settings-action settings-action--danger" id="resetAllBtn" type="button">Reset app</button>
      <p class="setting-hint">Unpairs this device and erases everything stored on it - your device key, paired accounts, and settings. You will need to set up from scratch to sign in again.</p>
    </section>

    <div class="modal-actions">
      <button class="btn btn-ghost" id="cancel">Close</button>
      <button class="btn btn-primary" id="save">Save</button>
    </div>
  `, (root, close) => {
      const toggle = root.querySelector("#appLockToggle");
      const select = root.querySelector("#lockAfterSel");
      const saveBtn = root.querySelector("#save");
      const cancelBtn = root.querySelector("#cancel");
      const clearBtn = root.querySelector("#clearUsernameBtn");
      const resetBtn = root.querySelector("#resetAllBtn");
      const busy = busyController(saveBtn);

      cancelBtn?.addEventListener("click", close);

      saveBtn?.addEventListener("click", async () => {
        busy.start("Saving...");
        try {
          const newEnabled = !!toggle.checked;
          const newMs = Math.max(0, parseInt(select.value, 10) || 0);
          await saveAppLockSettings({ enabled: newEnabled, ms: newMs });

          // Permission must be requested inside this click (user gesture).
          const notifyToggle = root.querySelector("#notifyToggle");
          if (notifyToggle) {
            const res = await Notifications.setEnabled(!!notifyToggle.checked);
            if (notifyToggle.checked && !res.enabled) {
              toast(res.permission === "denied"
                ? "Notifications blocked in browser settings"
                : "Notifications not enabled", "error");
            }
          }

          toast("Security settings updated", "success");
          close();
        } finally { busy.done(); }
      });

      // Only clear username (do not touch device key)
      clearBtn?.addEventListener("click", async () => {
        errorModal({
          title: "Clear username",
          message: "This only removes the saved username. You will be able to enter a new one next time.",
          primary: "Clear username",
          secondary: "Cancel",
          onPrimary: async () => {
            await clearSavedUsername();
            toast("Username cleared", "success");
          },
        });
      });

      // Full reset = explicit device key removal
      resetBtn?.addEventListener("click", async () => {
        errorModal({
          title: "Reset app (remove everything)",
          message: "This unpairs the device and erases everything stored on it. You will need to set up from scratch to sign in again. This cannot be undone.",
          primary: "Reset app",
          secondary: "Cancel",
          onPrimary: async () => {
            await this.confirmAndRestart("We will remove the device key and all local data. Continue?");
          },
        });
      });
      // Left-aligned: this is a form, and centring made each control's hint sit
      // in a different place from the control it describes.
    });
  }
}


/* ------------ Install gate ------------ */

// Replaces the whole UI until the app is installed. Re-rendered whenever the
// browser hands us (or takes away) an install prompt.
function renderInstallGate() {
  const container = document.querySelector(".container");
  if (!container) return;

  const canPrompt = Install.canPrompt();
  // The iOS share glyph, drawn inline so users can match it to the real button
  // rather than decode a description of it.
  const SHARE_ICON = `<svg class="ios-share-icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M12 3v12M8.5 6.5 12 3l3.5 3.5M6 12v7.5A1.5 1.5 0 0 0 7.5 21h9a1.5 1.5 0 0 0 1.5-1.5V12"/></svg>`;

  let bodyHtml;
  if (canPrompt) {
    // Chromium: one tap does the whole thing.
    bodyHtml = `<button class="button" id="installNow" type="button">Install</button>`;
  } else if (Install.isIOS() && Install.isIOSSafari()) {
    // Safari has moved this around. Older versions put Share directly in the
    // toolbar; newer ones tuck it behind a "More" (...) button. Naming both
    // beats describing one and leaving the other group hunting.
    const where = Install.shareButtonLocation() === "top"
      ? "at the top of the screen"
      : "at the bottom of the screen";
    bodyHtml = `
      <ol class="steps-list ios-steps">
        <li>Tap the Share button ${SHARE_ICON} ${where} - or the <strong>&#8943;</strong> menu, then <strong>Share</strong>.</li>
        <li>Scroll the list, or tap <strong>More</strong>, and choose <strong>Add to Home Screen</strong>.</li>
        <li>Tap <strong>Add</strong>, then open Tide from your Home Screen.</li>
      </ol>
      <p class="install-gate__hint" aria-hidden="true">
        ${Install.shareButtonLocation() === "top" ? "&uarr; It is up there" : "&darr; It is down there"}
      </p>`;
  } else if (Install.isIOS()) {
    // Chrome/Edge/Firefox on iOS CAN add to the Home Screen - they have been
    // able to since iOS 16.4. The old advice to switch to Safari sent people
    // on a detour they no longer need, so lead with this browser's own menu
    // and keep Safari as the fallback for versions that still lack it.
    bodyHtml = `
      <ol class="steps-list">
        <li>Tap the <strong>&#8943;</strong> menu (three dots).</li>
        <li>Tap <strong>Share</strong>.</li>
        <li>Scroll the list, or tap <strong>More</strong>, and choose <strong>Add to Home Screen</strong>.</li>
        <li>Tap <strong>Add</strong>, then open Tide from your Home Screen.</li>
      </ol>
      <p class="install-gate__note">
        Cannot find it? Open this page in Safari and use Share ${SHARE_ICON} &rarr; <strong>Add to Home Screen</strong>.
        <button class="button" id="openSafari" type="button">Open in Safari</button>
        <button class="link-btn" id="copyLink" type="button">copy the link</button>
      </p>`;
  } else {
    // Android/desktop without a captured prompt. The usual cause is an
    // insecure origin - browsers only offer installation over https - so say
    // that rather than leaving the missing button unexplained.
    const insecureNote = !window.isSecureContext
      ? `<p class="install-gate__note">One-tap install needs a secure (https) connection. Over plain http the browser will not offer it, so use the menu instead.</p>`
      : "";
    bodyHtml = `
      <ol class="steps-list">${Install.manualSteps().map((s) => `<li>${escapeHtml(s)}</li>`).join("")}</ol>
      ${insecureNote}`;
  }

  container.innerHTML = `
    <header class="brand">
      <img class="brand-logo" src="${defaultLogoUrl}" alt="Tide" />
      <h1 class="brand-title">Authenticator</h1>
    </header>
    <div class="install-gate">
      <p class="install-gate__title">${Install.isIOS() ? "Add Tide to your Home Screen" : "Install this app to continue"}</p>
      <p class="install-gate__body">
        ${Install.isIOS()
      // Deliberately not "in Safari": this same line shows in Chrome, Edge and
      // Firefox on iOS, and naming the wrong browser makes it read as advice
      // meant for someone else.
      ? "Tide has to be on your Home Screen before you can pair this device. An app on the Home Screen keeps its own storage, so a device paired here in this browser would not be available to it."
      : "Tide Authenticator has to be installed before you can pair this device. An installed app keeps your paired device from being cleared by the browser."}
      </p>
      ${bodyHtml}
    </div>
  `;

  container.querySelector("#installNow")?.addEventListener("click", async () => {
    const outcome = await Install.prompt();
    if (outcome === "accepted") toast("Installing Tide Authenticator", "success");
    renderInstallGate();
  });
  container.querySelector("#openSafari")?.addEventListener("click", () => {
    Install.openInSafari();
  });
  container.querySelector("#copyLink")?.addEventListener("click", async () => {
    const ok = await Install.copyLink();
    toast(ok ? "Link copied - now paste it in Safari" : "Could not copy - select the address bar instead", ok ? "success" : "error");
  });

  // Hide the floating controls - none of them apply before installation.
  document.querySelector(".tide-overlay")?.classList.add("hidden");
}

/* ------------ Startup key detection (non-prompting) ------------ */
async function detectDeviceKeyPresence() {
  try {
    const exists = await KeyManager.hasDeviceKey();
    return exists ? "present" : "absent";
  } catch {
    return "absent";
  }
}

/* ------------ Auth orchestration at app boundaries ------------ */

async function requireDeviceAuthIfNeeded(reason = "open_or_resume", opts = { force: false }) {
  if (authInFlight) return;
  if (suppressAuthPrompts) return;
  if (!appLockEnabled) return;

  const now = Date.now();

  // Force if caller asked OR if we've been backgrounded longer than lockAfterMs
  const exceededInactivity =
    typeof lockAfterMs === "number" &&
    lockAfterMs >= 0 &&
    lastBackgroundAt > 0 &&
    (now - lastBackgroundAt) >= lockAfterMs;

  const force = !!opts.force || exceededInactivity;

  // Avoid prompting over active modal unless forced
  if (!force && isModalOpen()) return;

  const sessionActive = !!KeyManager.isSessionActive?.();
  const hasCachedKey = !!KeyManager.peekCachedDeviceKey?.();

  const sessionExpired = !sessionActive;
  const needsUnlock = force || !hasCachedKey || sessionExpired;

  if (!needsUnlock) {
    lastAuthAt = now;
    KeyManager.extendSession?.();
    return;
  }

  if (!force && lastAuthAt && (now - lastAuthAt) < MIN_AUTH_INTERVAL_MS) return;

  try {
    authInFlight = true;
    const shouldForcePrompt = force || sessionExpired;
    const loaded = await KeyManager.loadDeviceKey({ forcePrompt: shouldForcePrompt });
    if (loaded) {
      lastAuthAt = Date.now();
      lastInteractionAt = lastAuthAt; // reset foreground idle clock on unlock
      KeyManager.extendSession?.();
      authSuppressedUntil = lastAuthAt + MIN_AUTH_INTERVAL_MS;
      lastBackgroundAt = 0; // successful unlock -> clear background marker
    }
  } catch (e) {
    console.warn("[auth] verification skipped:", e?.message || e);
  } finally {
    authInFlight = false;
  }
}

function wireAuthEvents() {
  document.addEventListener("visibilitychange", async () => {
    if (document.visibilityState === "hidden") {
      lastBackgroundAt = Date.now();
    } else if (document.visibilityState === "visible") {
      await requireDeviceAuthIfNeeded("visibilitychange_visible");
    }
  });
}

/* ------------ Foreground inactivity lock ------------ */
let lastInteractionAt = Date.now();
let inactivityCheckTimer = null;

function markUserActivity() {
  lastInteractionAt = Date.now();
}

function startInactivityWatcher() {
  if (inactivityCheckTimer) clearInterval(inactivityCheckTimer);
  if (!appLockEnabled) return;

  inactivityCheckTimer = setInterval(async () => {
    if (authInFlight) return;
    const now = Date.now();
    const idleMs = now - lastInteractionAt;
    if (lockAfterMs >= 0 && idleMs >= lockAfterMs) {
      await requireDeviceAuthIfNeeded("foreground_inactivity", { force: true });
      lastInteractionAt = Date.now(); // avoid rapid re-prompts
    }
  }, 1000);
}

function stopInactivityWatcher() {
  if (inactivityCheckTimer) { clearInterval(inactivityCheckTimer); inactivityCheckTimer = null; }
}

// Consider these as "activity"
["pointerdown", "click", "keydown", "touchstart", "wheel"].forEach(evt =>
  window.addEventListener(evt, markUserActivity, { passive: true })
);

/* ------------ Bootstrap ------------ */

async function bootstrap() {
  injectBaseStylesOnce();

  // Installation is a hard requirement - nothing else runs until it is met.
  // beforeinstallprompt often arrives after this point, so re-render on change.
  Install.init(() => { if (!Install.isAllowedToRun()) renderInstallGate(); });
  if (!Install.isAllowedToRun()) {
    renderInstallGate();
    return;
  }

  // Ask to be exempt from eviction. Runs after the install gate on purpose:
  // browsers grant this far more readily to an installed app, and Firefox
  // spends a permission prompt on the attempt. Not awaited - it protects
  // storage, it is not a precondition for using any of it.
  ensurePersisted();

  await loadAppLockSettings();
  await Notifications.load();
  startInactivityWatcher();

  try {
    const raw = window.location.href || window.location.search;
    const extracted = extractUrlFromDeepLink(raw);
    if (extracted) DeepLinkStore.set(extracted);
  } catch { }

  const app = new QRScannerApp(DeepLinkStore.has() ? ENTRY.DEEP_LINK : ENTRY.REGULAR);

  // Expose helpers to external handlers
  app.clearAppSettings = app.clearAppSettings.bind(app);
  app.goHome = app.goHome.bind(app);

  const presence = await detectDeviceKeyPresence();
  const firstRunDone = (await Storage.get("first_run_complete")) === "1";
  suppressAuthPrompts = presence === "absent" && !firstRunDone;

  wireAuthEvents();

  // If we were launched via deep link
  if (DeepLinkStore.has()) {
    const consumed = DeepLinkStore.consume();
    const httpsForHandshake = normalizeToHttpForHandshake(consumed);
    if (httpsForHandshake) {
      const busy = busyController(app.scanBtn);
      if (!busy.isActive()) busy.start("Preparing sign-in...");
      await beginHandshakeFromUrl(httpsForHandshake, app, { shouldReturn: true, trackForRetry: false });
      return;
    }
  }

  // On initial open, require auth if device key present (OWASP)
  if (presence === "present") {
    await requireDeviceAuthIfNeeded("initial_open", { force: true });
    // Launched from the "Scan QR code" app shortcut - go straight to the camera.
    if (/[?&]scan=1\b/.test(location.search)) app.scanOrUseDeepLink();
    return;
  }

  await app.showSetupWizardInitial();
}

/* ------------ Debug console & global error surface ------------ */
// Open with ?debug=1 (or set localStorage.tide_debug = "1") to get a full
// on-device console (eruda) - works on phones where DevTools is unavailable.
const DEBUG_MODE = /[?&#](debug|eruda)=1/.test(location.href) || (() => { try { return localStorage.getItem("tide_debug") === "1"; } catch { return false; } })();
if (DEBUG_MODE) {
  import("eruda").then((m) => (m.default || m).init()).catch((e) => console.warn("eruda load failed:", e));
}

// Uncaught errors always surface in the on-page status banner, debug or not.
function surfaceFatalError(message) {
  try {
    const el = document.getElementById("status");
    if (!el) return;
    el.classList.remove("hidden", "info", "success");
    el.classList.add("error");
    el.textContent = message;
  } catch { }
}
function describeErrorReason(r) {
  if (!r) return "Unknown error (see console)";
  if (typeof r === "string") return r;
  if (r.message) return r.message;
  if (typeof Event !== "undefined" && r instanceof Event) {
    return `Unhandled ${r.type || "event"} error${r.target?.url ? ` (${r.target.url})` : ""} - see console`;
  }
  try { return JSON.stringify(r); } catch { return String(r); }
}
window.addEventListener("error", (e) => {
  surfaceFatalError(`Error: ${e.message || describeErrorReason(e.error)}`);
});
window.addEventListener("unhandledrejection", (e) => {
  surfaceFatalError(`Error: ${describeErrorReason(e.reason)}`);
});

document.addEventListener("DOMContentLoaded", async () => { await bootstrap(); });

/* ------------ Helpers ------------ */
function httpToWs(url) {
  let newUrl = url;
  if (newUrl.startsWith("https://")) newUrl = newUrl.replace(/^https:/, "wss:");
  else if (newUrl.startsWith("http://")) newUrl = newUrl.replace(/^http:/, "ws:");
  return newUrl;
}
async function userExists(url, username) {
  try {
    const newUrl = new URL(url);
    const uid = await Serialization.GetUID(username);
    const res = await fetch(`${newUrl.origin}/Network/Authentication/Users/Exists/${uid}`);
    const ok = await res.json();
    return Boolean(ok);
  } catch {
    return false;
  }
}

/* ------------ PWA SW ------------ */
// Service worker registration is injected at build time by vite-plugin-pwa
// (registerType: "autoUpdate").